import { getLocale, type Locale } from '../../../i18n';
import { cm } from './messages';

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The local calendar day an instant falls on, as a sortable key. The API speaks UTC instants;
 *  the reader lives in a wall-clock day, so a slot at 23:30 local belongs to that day even
 *  though its UTC date is the next one. */
export function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

export function formatDate(date: Date, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(date);
}

export function formatTime(date: Date, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(date);
}

function dayOffset(date: Date, now: Date): number {
  return Math.round((startOfDay(date).getTime() - startOfDay(now).getTime()) / 86_400_000);
}

/** "Hoje", "Ontem", "Amanhã", or the bare date beyond those -- the word alone, for a badge or a
 *  sentence that supplies its own date. */
export function dayLabel(date: Date, now: Date): string {
  const offset = dayOffset(date, now);
  if (offset === 0) return cm('day.today');
  if (offset === -1) return cm('day.yesterday');
  if (offset === 1) return cm('day.tomorrow');
  return formatDate(date, getLocale());
}

/** "Hoje · 17/09/2026", "Ontem · 16/09/2026", or the bare date beyond those. */
export function dayHeading(date: Date, now: Date): string {
  const offset = dayOffset(date, now);
  const formatted = formatDate(date, getLocale());
  if (offset === 0) return `${cm('day.today')} · ${formatted}`;
  if (offset === -1) return `${cm('day.yesterday')} · ${formatted}`;
  if (offset === 1) return `${cm('day.tomorrow')} · ${formatted}`;
  return formatted;
}
